import React, { useEffect, useState } from "react";
import styles from "../styles/Products.module.css";
import modalStyles from "../styles/ProductModal.module.css";
import { API } from "../config";
import { useNavigate } from "react-router-dom";

const Products = () => {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [displayMode, setDisplayMode] = useState("table");

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showOutOfStockOnly, setShowOutOfStockOnly] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(2);

  const [formData, setFormData] = useState({
    product_id: "",
    name: "",
    description: "",
    price: "",
    color: "Red",
    active: false,
    stock: "Yes",
  });

  /* ================= FETCH ================= */
  const fetchProducts = async () => {
    const res = await fetch(API.PRODUCT);
    const data = await res.json();

    const normalized = data.map((p) => ({
      ...p,
      active: p.active === true || p.active === 1 || p.active === "1",
    }));

    setProducts(normalized);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  /* ================= FILTER ================= */
  const filteredProducts = products.filter((p) => {
    if (statusFilter === "ACTIVE" && !p.active) return false;
    if (statusFilter === "INACTIVE" && p.active) return false;
    if (showOutOfStockOnly && p.stock !== "No") return false;
    return true;
  });

  const productCount = filteredProducts.length;
  const totalAmount = filteredProducts.reduce(
    (sum, p) => sum + Number(p.price || 0),
    0
  );

  /* ================= PAGINATION ================= */
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  /* keep page valid */
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  /* ================= FORM ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEdit = Boolean(editingProduct);
    const url = isEdit
      ? `${API.PRODUCT}/${editingProduct.id}`
      : API.PRODUCT;

    const method = isEdit ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (!res.ok) {
      alert("Save failed");
      return;
    }

    await fetchProducts();
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData(product);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await fetch(`${API.PRODUCT}/${id}`, { method: "DELETE" });
    fetchProducts();
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL products?")) return;
    await fetch(`${API.PRODUCT}/deleteAll`, { method: "DELETE" });
    setProducts([]);
  };

  /* ================= UI ================= */
  return (
    <div className={styles.productsPage}>
      <div className={styles.productsCard}>
        {/* TOP BAR */}
        <div className={styles.topBar}>
          <button className={styles.dashboardBtn} onClick={() => navigate(-1)}>
            ⬅ Dashboard
          </button>

          <button
            className={styles.toggleBtn}
            onClick={() =>
              setDisplayMode(displayMode === "table" ? "tile" : "table")
            }
          >
            {displayMode === "table" ? "🧱 Tile View" : "📋 Table View"}
          </button>
        </div>

        {/* HEADER */}
        <div className={styles.header}>
          <h2>📦 Products</h2>

          <div className={styles.headerButtons}>
            <button
              className={styles.deleteAllBtn}
              onClick={handleDeleteAll}
              disabled={products.length === 0}
            >
              Delete All
            </button>

            <button
              className={styles.createBtn}
              onClick={() => {
                setEditingProduct(null);
                setFormData({
                  product_id: "",
                  name: "",
                  description: "",
                  price: "",
                  color: "Red",
                  active: false,
                  stock: "Yes",
                });
                setShowModal(true);
              }}
            >
              + Create Product
            </button>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className={styles.filterBar}>
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="ALL">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>

          <label>
            <input
              type="checkbox"
              checked={showOutOfStockOnly}
              onChange={(e) => {
                setShowOutOfStockOnly(e.target.checked);
                setCurrentPage(1);
              }}
            />{" "}
            Out of Stock
          </label>

          <div className={styles.counts}>
            <span><strong>Products:</strong> {productCount}</span>
            <span><strong>Total:</strong> ₹{totalAmount}</span>
          </div>
        </div>

        {/* TABLE VIEW */}
        {displayMode === "table" && (
          <table className={styles.productsTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Product ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Price</th>
                <th>Color</th>
                <th>Status</th>
                <th>Stock</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.map((p, i) => (
                <tr key={p.id}>
                  <td>{indexOfFirstItem + i + 1}</td>
                  <td>{p.product_id}</td>
                  <td>{p.name}</td>
                  <td>{p.description || "-"}</td>
                  <td>₹ {p.price}</td>
                  <td>{p.color}</td>
                  <td>{p.active ? "Active" : "Inactive"}</td>
                  <td>{p.stock}</td>
                  <td className={styles.actions}>
                    <button
                      className={styles.editBtn}
                      onClick={() => handleEdit(p)}
                    >
                      Edit
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(p.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* TILE VIEW */}
        {displayMode === "tile" && (
          <div className={styles.productGrid}>
            {currentProducts.map((p) => (
              <div key={p.id} className={styles.productTile}>
                
                {/* IMAGE PLACEHOLDER */}
                <div className={styles.imagePlaceholder}>🛍️</div>

                {/* DETAILS */}
                <div className={styles.details}>
                  <h4>{p.name}</h4>

                  <p className={styles.desc}>
                    {p.description || "No description available"}
                  </p>

                  <p className={styles.price}>₹ {p.price}</p>

                  <div className={styles.meta}>
                    <div>🎨 Color: {p.color}</div>
                    <div>📦 Stock: {p.stock}</div>
                    <div>
                      ⚡ Status:{" "}
                      <strong>{p.active ? "Active" : "Inactive"}</strong>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}


        {/* PAGINATION */}
        <div className={styles.pagination}>
          <label>Items per page:</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={2}>2</option>
            <option value={4}>4</option>
            <option value={6}>6</option>
            <option value={8}>8</option>
          </select>

          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            Prev
          </button>

          {Array.from({ length: totalPages || 1 }, (_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? styles.activePage : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((p) => Math.min(p + 1, totalPages))
            }
          >
            Next
          </button>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className={modalStyles.overlay}>
          <div className={modalStyles.modal}>
            <h3>{editingProduct ? "Edit Product" : "Create Product"}</h3>

            <form onSubmit={handleSubmit} className={modalStyles.form}>
              <input name="product_id" value={formData.product_id} onChange={handleChange} required placeholder="Product ID"/>
              <input name="name" value={formData.name} onChange={handleChange} required placeholder="Product Name"/> 
              <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Product Description"/>
              <input type="number" name="price" value={formData.price} onChange={handleChange} required placeholder="Product Price"/>

              <select name="color" value={formData.color} onChange={handleChange}>
                <option>Red</option>
                <option>Blue</option>
                <option>Green</option>
                <option>Yellow</option>
              </select>

              <label>
                <input type="checkbox" name="active" checked={formData.active} onChange={handleChange} /> Active
              </label>

              <label>
                <input type="radio" name="stock" value="Yes" checked={formData.stock === "Yes"} onChange={handleChange} /> In Stock
              </label>

              <label>
                <input type="radio" name="stock" value="No" checked={formData.stock === "No"} onChange={handleChange} /> Out of Stock
              </label>

              <div className={modalStyles.actions}>
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
